import {useMemo} from "react";
import {useDispatch} from "react-redux";

import type {IMarker} from "@pg-mono/open-street-map";
import {LatLngTuple} from "@pg-mono/open-street-map";

import {setActivePoi} from "../actions/set_poi_travel_directions";
import {useGetOfferDetailPoiByTypeQuery} from "../api/get_offer_detail_poi";
import {POI_DISTANCE_DEFAULT_VALUE_IN_METERS} from "../constants/pois_defaults";
import {activeTransportMarkerUrls} from "../constants/transport_marker_urls";
import {IPoi} from "../types/IPoi";
import {PoiType, TransportPoiType} from "../types/PoiType";
import {createGetOsmPoiMarker, OsmPoiClickHandler} from "../utils/create_get_osm_poi_marker";
import {usePois} from "./use_pois";
import {useTransportPois, useTransportPoisStats} from "./use_transport_pois";

const education_pin = require("../images/pins/education_pin.svg");
const sport_pin = require("../images/pins/sport_pin.svg");
const health_pin = require("../images/pins/health_pin.svg");
const entertainment_pin = require("../images/pins/entertainment_pin.svg");
const shops_pin = require("../images/pins/shops_pin.svg");
const food_pin = require("../images/pins/food_pin.svg");

type IHook = Record<string, IMarker[]>;

export interface IUsePoisMarkersOptions {
    disableInitiallyOpenedPoiId?: boolean;
    checkedPoiTypes: PoiType[];
    radiusInMeters?: number;
    onPoiClick?: OsmPoiClickHandler;
}

export const usePoisMarkers = (offerId: number, offerGeoPointCoordinates: LatLngTuple, options?: IUsePoisMarkersOptions): IHook => {
    const dispatch = useDispatch();

    const {data} = useGetOfferDetailPoiByTypeQuery(
        {
            offerId: offerId,
            poiTypes: options?.checkedPoiTypes
        },
        {
            skip: !options?.checkedPoiTypes || options.checkedPoiTypes.length === 0
        }
    );
    const {poisFromApi, transportPoisFromApi} = useMemo(() => {
        const poisFromApi = (options?.checkedPoiTypes?.length ? data?.poi?.pois || {} : {}) as Record<PoiType, IPoi[] | Record<TransportPoiType, IPoi[]>>;
        const transportPoisFromApi = (options?.checkedPoiTypes?.length ? data?.poi?.pois.transport || {} : {}) as Record<TransportPoiType, IPoi[]>;

        return {
            poisFromApi,
            transportPoisFromApi
        };
    }, [data, options?.checkedPoiTypes?.join(",")]);

    const {educationPois, entertainmentPois, foodPois, healthPois, sportPois, shopPois} = usePois(poisFromApi, options?.radiusInMeters);
    const {transportPois} = useTransportPois(transportPoisFromApi, {
        longitude: offerGeoPointCoordinates[0],
        latitude: offerGeoPointCoordinates[1],
        radius: options?.radiusInMeters || POI_DISTANCE_DEFAULT_VALUE_IN_METERS
    });
    const getPoiMarker = createGetOsmPoiMarker((poi, poiType) => dispatch(setActivePoi(poi, poiType)), offerGeoPointCoordinates, options?.onPoiClick);
    const {subwayPoi, railwayPoi, tramPoi, busPoi} = useTransportPoisStats();

    const transportPoisMarkers = useMemo(() => {
        const transportPoiKeys = Object.keys(transportPois || {}) as TransportPoiType[];
        // INFO: This is related to the business requirement to initially open marker InfoWindow for "best" transport POI
        const initiallyOpenedTransportPoiId = options?.disableInitiallyOpenedPoiId ? undefined : subwayPoi?.id || railwayPoi?.id || busPoi?.id || tramPoi?.id;

        return !!transportPois
            ? transportPoiKeys.reduce((acc, key) => {
                  return acc.concat(
                      ...transportPois[key].map((poi) => {
                          if (poi.id === initiallyOpenedTransportPoiId) {
                              return getPoiMarker(poi, PoiType.TRANSPORT, activeTransportMarkerUrls[key], {initiallyOpened: true}, key);
                          }
                          return getPoiMarker(poi, PoiType.TRANSPORT, activeTransportMarkerUrls[key], {}, key);
                      })
                  );
              }, [] as IMarker[])
            : [];
    }, [options?.disableInitiallyOpenedPoiId, options?.onPoiClick, subwayPoi?.id, railwayPoi?.id, tramPoi?.id, busPoi?.id, transportPois]);

    const educationPoisMarkers = useMemo(() => {
        return educationPois.map((poi) => getPoiMarker(poi, PoiType.EDUCATION, education_pin));
    }, [educationPois, options?.onPoiClick]);

    const entertainmentPoisMarkers = useMemo(() => {
        return entertainmentPois.map((poi) => getPoiMarker(poi, PoiType.ENTERTAINMENT, entertainment_pin));
    }, [entertainmentPois, options?.onPoiClick]);

    const foodPoisMarkers = useMemo(() => {
        return foodPois.map((poi) => getPoiMarker(poi, PoiType.FOOD, food_pin));
    }, [foodPois, options?.onPoiClick]);

    const healthPoisMarkers = useMemo(() => {
        return healthPois.map((poi) => getPoiMarker(poi, PoiType.HEALTH, health_pin));
    }, [healthPois, options?.onPoiClick]);

    const sportPoisMarkers = useMemo(() => {
        return sportPois.map((poi) => getPoiMarker(poi, PoiType.SPORT, sport_pin));
    }, [sportPois, options?.onPoiClick]);

    const shopPoisMarkers = useMemo(() => {
        return shopPois.map((poi) => getPoiMarker(poi, PoiType.SHOPS, shops_pin));
    }, [shopPois, options?.onPoiClick]);

    return useMemo(() => {
        return {
            [PoiType.EDUCATION]: educationPoisMarkers,
            [PoiType.ENTERTAINMENT]: entertainmentPoisMarkers,
            [PoiType.FOOD]: foodPoisMarkers,
            [PoiType.HEALTH]: healthPoisMarkers,
            [PoiType.SHOPS]: shopPoisMarkers,
            [PoiType.SPORT]: sportPoisMarkers,
            [PoiType.TRANSPORT]: transportPoisMarkers
        };
    }, [educationPoisMarkers, entertainmentPoisMarkers, foodPoisMarkers, healthPoisMarkers, shopPoisMarkers, sportPoisMarkers, transportPoisMarkers]);
};
import {useMemo} from "react";
import {useDispatch} from "react-redux";

import type {IMarker} from "@pg-mono/open-street-map";
import {LatLngTuple} from "@pg-mono/open-street-map";

import {setActivePoi} from "../actions/set_poi_travel_directions";
import {useGetOfferDetailPoiByTypeQuery} from "../api/get_offer_detail_poi";
import {POI_DISTANCE_DEFAULT_VALUE_IN_METERS} from "../constants/pois_defaults";
import {activeTransportMarkerUrls} from "../constants/transport_marker_urls";
import {IPoi} from "../types/IPoi";
import {PoiType, TransportPoiType} from "../types/PoiType";
import {createGetOsmPoiMarker, OsmPoiClickHandler} from "../utils/create_get_osm_poi_marker";
import {usePois} from "./use_pois";
import {useTransportPois, useTransportPoisStats} from "./use_transport_pois";

const education_pin = require("../images/pins/education_pin.svg");
const sport_pin = require("../images/pins/sport_pin.svg");
const health_pin = require("../images/pins/health_pin.svg");
const entertainment_pin = require("../images/pins/entertainment_pin.svg");
const shops_pin = require("../images/pins/shops_pin.svg");
const food_pin = require("../images/pins/food_pin.svg");

type IHook = Record<string, IMarker[]>;

export interface IUsePoisMarkersOptions {
    disableInitiallyOpenedPoiId?: boolean;
    checkedPoiTypes: PoiType[];
    radiusInMeters?: number;
    onPoiClick?: OsmPoiClickHandler;
}

export const usePoisMarkers = (offerId: number, offerGeoPointCoordinates: LatLngTuple, options?: IUsePoisMarkersOptions): IHook => {
    const dispatch = useDispatch();

    const {data} = useGetOfferDetailPoiByTypeQuery(
        {
            offerId: offerId,
            poiTypes: options?.checkedPoiTypes
        },
        {
            skip: !options?.checkedPoiTypes || options.checkedPoiTypes.length === 0
        }
    );
    const {poisFromApi, transportPoisFromApi} = useMemo(() => {
        const poisFromApi = (options?.checkedPoiTypes?.length ? data?.poi?.pois || {} : {}) as Record<PoiType, IPoi[] | Record<TransportPoiType, IPoi[]>>;
        const transportPoisFromApi = (options?.checkedPoiTypes?.length ? data?.poi?.pois.transport || {} : {}) as Record<TransportPoiType, IPoi[]>;

        return {
            poisFromApi,
            transportPoisFromApi
        };
    }, [data, options?.checkedPoiTypes?.join(",")]);

    const {educationPois, entertainmentPois, foodPois, healthPois, sportPois, shopPois} = usePois(poisFromApi, options?.radiusInMeters);
    const {transportPois} = useTransportPois(transportPoisFromApi, {
        longitude: offerGeoPointCoordinates[0],
        latitude: offerGeoPointCoordinates[1],
        radius: options?.radiusInMeters || POI_DISTANCE_DEFAULT_VALUE_IN_METERS
    });
    const getPoiMarker = createGetOsmPoiMarker((poi, poiType) => dispatch(setActivePoi(poi, poiType)), offerGeoPointCoordinates, options?.onPoiClick);
    const {subwayPoi, railwayPoi, tramPoi, busPoi} = useTransportPoisStats();

    const transportPoisMarkers = useMemo(() => {
        const transportPoiKeys = Object.keys(transportPois || {}) as TransportPoiType[];
        // INFO: This is related to the business requirement to initially open marker InfoWindow for "best" transport POI
        const initiallyOpenedTransportPoiId = options?.disableInitiallyOpenedPoiId ? undefined : subwayPoi?.id || railwayPoi?.id || busPoi?.id || tramPoi?.id;

        return !!transportPois
            ? transportPoiKeys.reduce((acc, key) => {
                  return acc.concat(
                      ...transportPois[key].map((poi) => {
                          if (poi.id === initiallyOpenedTransportPoiId) {
                              return getPoiMarker(poi, PoiType.TRANSPORT, activeTransportMarkerUrls[key], {initiallyOpened: true}, key);
                          }
                          return getPoiMarker(poi, PoiType.TRANSPORT, activeTransportMarkerUrls[key], {}, key);
                      })
                  );
              }, [] as IMarker[])
            : [];
    }, [options?.disableInitiallyOpenedPoiId, options?.onPoiClick, subwayPoi?.id, railwayPoi?.id, tramPoi?.id, busPoi?.id, transportPois]);

    const educationPoisMarkers = useMemo(() => {
        return educationPois.map((poi) => getPoiMarker(poi, PoiType.EDUCATION, education_pin));
    }, [educationPois, options?.onPoiClick]);

    const entertainmentPoisMarkers = useMemo(() => {
        return entertainmentPois.map((poi) => getPoiMarker(poi, PoiType.ENTERTAINMENT, entertainment_pin));
    }, [entertainmentPois, options?.onPoiClick]);

    const foodPoisMarkers = useMemo(() => {
        return foodPois.map((poi) => getPoiMarker(poi, PoiType.FOOD, food_pin));
    }, [foodPois, options?.onPoiClick]);

    const healthPoisMarkers = useMemo(() => {
        return healthPois.map((poi) => getPoiMarker(poi, PoiType.HEALTH, health_pin));
    }, [healthPois, options?.onPoiClick]);

    const sportPoisMarkers = useMemo(() => {
        return sportPois.map((poi) => getPoiMarker(poi, PoiType.SPORT, sport_pin));
    }, [sportPois, options?.onPoiClick]);

    const shopPoisMarkers = useMemo(() => {
        return shopPois.map((poi) => getPoiMarker(poi, PoiType.SHOPS, shops_pin));
    }, [shopPois, options?.onPoiClick]);

    return useMemo(() => {
        return {
            [PoiType.EDUCATION]: educationPoisMarkers,
            [PoiType.ENTERTAINMENT]: entertainmentPoisMarkers,
            [PoiType.FOOD]: foodPoisMarkers,
            [PoiType.HEALTH]: healthPoisMarkers,
            [PoiType.SHOPS]: shopPoisMarkers,
            [PoiType.SPORT]: sportPoisMarkers,
            [PoiType.TRANSPORT]: transportPoisMarkers
        };
    }, [educationPoisMarkers, entertainmentPoisMarkers, foodPoisMarkers, healthPoisMarkers, shopPoisMarkers, sportPoisMarkers, transportPoisMarkers]);
};
